import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 5: ElasticSearch Master Search Engine
 * - Users -> Load Balancer -> Django Query API -> ML/NLP -> ElasticSearch cluster
 * - Atlassian/Splunk/Solr/Other sources -> Logstash -> Django Ingest API -> ElasticSearch
 * - Storage + SQL + supporting services
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 8,
    fontWeight: 800,
    color: "#0f172a",
    width: 240,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0b2e59",
    width: 130,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 130,
    textAlign: "center",
  },
  ml: {
    background: "#f5f3ff",
    border: "2px solid #a855f7",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4c1d95",
    width: 120,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 120,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#05333b",
    width: 140,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 8,
    fontWeight: 700,
    color: "#92400e",
    width: 160,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project5FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background container
      {
        id: "bg-gcp",
        position: { x: 280, y: 40 },
        data: { label: "" },
        style: {
          width: 860,
          height: 700,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-sources",
        position: { x: 20, y: 300 },
        data: { label: "" },
        style: {
          width: 220,
          height: 300,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Users
      {
        id: "users",
        position: { x: 40, y: 70 },
        data: { label: "End Users" },
        style: nodeStyle.compute,
      },
      {
        id: "query",
        position: { x: 40, y: 170 },
        data: { label: "① Query" },
        style: nodeStyle.note,
      },

      // GCP header
      {
        id: "sec-gcp",
        position: { x: 300, y: 50 },
        data: { label: "GCP CLOUD PLATFORM" },
        style: { ...nodeStyle.section, width: 260 },
      },

      // Load balancer
      { id: "lb", position: { x: 340, y: 130 }, data: { label: "Load Balancer" }, style: nodeStyle.net },

      // API layer
      { id: "sec-api", position: { x: 340, y: 210 }, data: { label: "DJANGO API" }, style: nodeStyle.section },
      { id: "query-api", position: { x: 340, y: 280 }, data: { label: "Query API" }, style: nodeStyle.ops },
      { id: "ingest-api", position: { x: 520, y: 280 }, data: { label: "Ingest API" }, style: nodeStyle.ops },

      // ML/NLP
      { id: "preprocess", position: { x: 560, y: 340 }, data: { label: "② Preprocess" }, style: nodeStyle.note },
      { id: "sec-ml", position: { x: 380, y: 380 }, data: { label: "ML / NLP" }, style: nodeStyle.section },
      { id: "ml", position: { x: 380, y: 450 }, data: { label: "ML" }, style: nodeStyle.ml },
      { id: "nlp", position: { x: 540, y: 450 }, data: { label: "NLP" }, style: nodeStyle.ml },

      // ElasticSearch
      { id: "search", position: { x: 700, y: 390 }, data: { label: "③ Search" }, style: nodeStyle.note },
      { id: "sec-es", position: { x: 700, y: 430 }, data: { label: "ElasticSearch" }, style: nodeStyle.section },
      { id: "es-master", position: { x: 720, y: 515 }, data: { label: "Master" }, style: nodeStyle.data },
      { id: "es-d1", position: { x: 660, y: 608 }, data: { label: "Data 1" }, style: nodeStyle.data },
      { id: "es-d2", position: { x: 820, y: 608 }, data: { label: "Data 2" }, style: nodeStyle.data },

      // Storage + SQL
      { id: "storage", position: { x: 380, y: 590 }, data: { label: "Storage" }, style: nodeStyle.compute },
      { id: "sql", position: { x: 380, y: 660 }, data: { label: "SQL" }, style: nodeStyle.data },

      // Supporting services
      { id: "vpc", position: { x: 980, y: 160 }, data: { label: "VPC" }, style: nodeStyle.net },
      { id: "log", position: { x: 980, y: 240 }, data: { label: "Log" }, style: nodeStyle.net },
      { id: "mon", position: { x: 980, y: 320 }, data: { label: "Mon" }, style: nodeStyle.net },
      { id: "iam", position: { x: 980, y: 400 }, data: { label: "IAM" }, style: nodeStyle.net },

      // Source systems
      {
        id: "sec-sources",
        position: { x: 40, y: 310 },
        data: { label: "SOURCE SYSTEMS" },
        style: { ...nodeStyle.section, width: 180, borderColor: "#fdba74" },
      },
      { id: "atlassian", position: { x: 40, y: 360 }, data: { label: "Atlassian Tools" }, style: nodeStyle.compute },
      { id: "splunk", position: { x: 40, y: 420 }, data: { label: "Splunk Bucket" }, style: nodeStyle.compute },
      { id: "solr", position: { x: 40, y: 480 }, data: { label: "Apache Solr" }, style: nodeStyle.compute },
      { id: "others", position: { x: 40, y: 540 }, data: { label: "Other Sources" }, style: nodeStyle.compute },
      { id: "logstash", position: { x: 260, y: 460 }, data: { label: "Logstash" }, style: nodeStyle.ops },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const purple = { stroke: "#7c3aed" };
    const orange = { stroke: "#f59e0b" };

    return [
      mkEdge("e-users-query", "users", "query", "", { style: { ...blue } }),
      mkEdge("e-query-lb", "query", "lb", "① Query", { style: { ...blue, strokeWidth: 5 } }),
      mkEdge("e-lb-query", "lb", "query-api", "", { style: { ...green } }),
      mkEdge("e-query-ml", "query-api", "ml", "② Preprocess", { style: { ...purple, strokeWidth: 5 } }),
      mkEdge("e-ml-nlp", "ml", "nlp", "", { style: { ...purple } }),
      mkEdge("e-nlp-es", "nlp", "es-master", "③ Search", { style: { ...orange, strokeWidth: 5 } }),
      mkEdge("e-es-master-d1", "es-master", "es-d1", "", { style: { ...orange } }),
      mkEdge("e-es-master-d2", "es-master", "es-d2", "", { style: { ...orange } }),
      mkEdge("e-query-storage", "query-api", "storage", "", { style: { ...blue } }),
      mkEdge("e-storage-sql", "storage", "sql", "", { style: { ...blue } }),
      mkEdge("e-atlassian-logstash", "atlassian", "logstash", "Ingest", { style: { ...blue } }),
      mkEdge("e-splunk-logstash", "splunk", "logstash", "Ingest", { style: { ...blue } }),
      mkEdge("e-solr-logstash", "solr", "logstash", "Ingest", { style: { ...blue } }),
      mkEdge("e-others-logstash", "others", "logstash", "Ingest", { style: { ...blue } }),
      mkEdge("e-logstash-ingest", "logstash", "ingest-api", "Normalize/Enrich", {
        style: { ...green, strokeWidth: 4 },
      }),
      mkEdge("e-ingest-es", "ingest-api", "es-master", "Index", {
        style: { ...orange, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "880px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.4}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
